import { X } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";

type ModalProps = {
	open: boolean;
	onClose: () => void;
	title?: ReactNode;
	subtitle?: ReactNode;
	rightHeader?: ReactNode;
	footer?: ReactNode;
	children: ReactNode;
	size?: "md" | "lg";
};

export function Modal({ open, onClose, title, subtitle, rightHeader, footer, children, size = "lg" }: ModalProps) {
	const dialogRef = useRef<HTMLDivElement>(null);
	// Keep latest onClose in a ref so the open-effect doesn't depend on it.
	// Parents commonly pass a fresh inline arrow on every render (e.g. when a
	// WebSocket update reflows their state); without the ref, the effect tore
	// down and re-focused the dialog on every parent re-render — which blurred
	// any <input> the user was typing into.
	const onCloseRef = useRef(onClose);
	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				onCloseRef.current();
			}
		};
		window.addEventListener("keydown", onKey);
		const previouslyFocused = document.activeElement as HTMLElement | null;
		dialogRef.current?.focus();
		document.body.style.overflow = "hidden";
		return () => {
			window.removeEventListener("keydown", onKey);
			document.body.style.overflow = "";
			previouslyFocused?.focus?.();
		};
	}, [open]);

	if (!open) return null;

	const widthClass = size === "lg" ? "max-w-3xl" : "max-w-md";

	return (
		<div
			className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[#060912]/60 px-3 py-4 backdrop-blur-[2px] sm:px-4 sm:py-[8vh]"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
			role="presentation"
		>
			<div
				ref={dialogRef}
				tabIndex={-1}
				role="dialog"
				aria-modal="true"
				className={`flex w-full ${widthClass} flex-col overflow-hidden rounded-[14px] border border-hairline bg-paper shadow-[0_24px_64px_-16px_rgba(6,9,18,0.55)] outline-none`}
				style={{ maxHeight: "min(88vh, 760px)" }}
			>
				{(title || rightHeader) && (
					<header className="flex items-center justify-between gap-3 border-b border-hairline bg-paper py-2 pr-2 pl-4">
						<div className="min-w-0 flex-1">
							{title && (
								<div className="flex flex-wrap items-center gap-2 text-[15px] font-semibold text-ink">{title}</div>
							)}
							{subtitle && <div className="mt-0.5 truncate text-xs text-muted">{subtitle}</div>}
						</div>
						<div className="flex shrink-0 items-center gap-2">
							{rightHeader}
							<button
								onClick={onClose}
								className="flex size-tap items-center justify-center rounded-md text-muted hover:bg-raised hover:text-ink"
								aria-label="Close"
							>
								<X className="size-4" aria-hidden />
							</button>
						</div>
					</header>
				)}
				<div className="flex min-h-0 flex-1 flex-col">{children}</div>
				{footer && (
					<footer className="flex items-center justify-end gap-2 border-t border-hairline bg-paper px-4 py-2.5">
						{footer}
					</footer>
				)}
			</div>
		</div>
	);
}
